/**
 * Shared chrome — the programme masthead, tab bar, section rules, and the
 * small honest-data atoms (stale badge, empty state) every screen leans on.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { asOfLabel } from '../lib/feeds';
import { APP_SHARE, share, type ShareMoment } from '../lib/share';
import type { CommunityReport, MatchPhase } from '../lib/types';

export type TabId = 'now' | 'matches' | 'history' | 'terrace' | 'squad';

// ── Original COYG matchday mark — not the club crest ───────────────────────
export const COYGLogo = ({ color = '#EF0107', height = 22 }: { color?: string; height?: number }) => {
  const w = Math.round(height * 2.35);
  return (
    <svg width={w} height={height} viewBox="0 0 94 40" fill="none" aria-hidden="true">
      <path d="M5 5h84v30H5z" stroke={color} strokeWidth="3" />
      <path d="M14 31V9M80 31V9" stroke={color} strokeWidth="2" opacity=".55" />
      <text x="47" y="28" textAnchor="middle" fill={color} fontFamily="Barlow Condensed, sans-serif" fontSize="25" fontWeight="800" letterSpacing="1">COYG</text>
    </svg>
  );
};

// ── Masthead ─────────────────────────────────────────────────────────────────

/**
 * Share the app itself — a quiet affordance always in the masthead. Native
 * sheet on mobile, clipboard copy with a brief confirmation on desktop.
 */
const MastheadShare = ({ moment = APP_SHARE }: { moment?: ShareMoment }) => {
  const [flash, setFlash] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const onShare = async () => {
    const result = await share(moment);
    if (result === 'failed') return;
    setFlash(result === 'copied' ? 'Link copied' : result === 'cancelled' ? 'Share cancelled' : 'Shared');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlash(null), 1600);
  };

  return (
    <button className="masthead-share" onClick={onShare} aria-label="Share COYG">
      {flash ? (
        <span className="masthead-share-flash" role="status" aria-live="polite">{flash}</span>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
        </svg>
      )}
    </button>
  );
};

const editionLabel = (live = false): string => {
  if (live) return 'Match edition';
  try {
    const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'Europe/London' }).format(new Date());
    return `${weekday} edition`;
  } catch {
    return 'Today’s edition';
  }
};

const editionDateLabel = (): string => {
  try {
    const date = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/London',
    }).format(new Date());
    return `${date} · London`;
  } catch {
    return 'London';
  }
};

const MastheadMore = () => (
  <details className="masthead-more">
    <summary aria-label="More: about, help and source">More</summary>
    <div className="masthead-menu">
      <strong>Open. Free. Unofficial.</strong>
      <p>News across Arsenal, a year-by-year club archive, legends and dated men’s and women’s squad records. Automatic fixtures cover the men’s Premier League. COYG is a supporter project and is not affiliated with or endorsed by Arsenal Football Club.</p>
      <nav aria-label="About COYG">
        <a className="mobile-menu-wiki" href="/wiki">Wiki</a>
        <a href="/terms.html">Terms</a>
        <a href="/privacy.html">Privacy</a>
        <a href="/north-bank/all?safety=1">Help &amp; safety</a>
        <a href="/coyg-source.zip" download>Source</a>
      </nav>
      <details className="mobile-install-guide"><summary>Add COYG to your home screen</summary><p>On iPhone, open COYG in Safari, tap Share, then Add to Home Screen. On Android, use your browser’s Install app or Add to Home screen option. COYG opens in its own window where supported.</p></details>
    </div>
  </details>
);

export const Masthead = ({ live, right, share = true, shareMoment, mobileTitle }: { live?: boolean; right?: ReactNode; share?: boolean; shareMoment?: ShareMoment; mobileTitle?: string }) => (
  <header className={`masthead${live ? ' masthead--live' : ''}`}>
    <a className="app-skip-link" href="#main">Skip to content</a>
    <div className="masthead-brand">
      <span className="masthead-logo"><COYGLogo color="var(--rt-accent, #d82320)" height={22} /></span>
      <span className="masthead-name">{editionLabel(live)}</span>
      {mobileTitle && <span className="mobile-page-title">{mobileTitle}</span>}
    </div>
    <div className="masthead-right">
      <span className="masthead-edition-meta">{editionDateLabel()}</span>
      {live && (
        <span className="live-chip">
          <span className="live-dot pulse" />
          Live
        </span>
      )}
      {right}
      <a className="masthead-wiki" href="/wiki">Wiki</a>
      {/* App-share only when no detail is open; on a detail the screen's own
          contextual Share is the single, more relevant button. */}
      {share && <MastheadShare moment={shareMoment} />}
      <MastheadMore />
    </div>
  </header>
);

// ── Tab bar ──────────────────────────────────────────────────────────────────
const TAB_DEFS: Array<{ id: TabId; label: string; icon: (a: boolean) => ReactNode }> = [
  {
    id: 'now',
    label: 'Now',
    icon: (a) => (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" />
      </svg>
    ),
  },
  {
    id: 'matches',
    label: 'Matches',
    icon: (a) => (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
  },
  {
    id: 'terrace',
    label: 'North Bank',
    icon: (a) => (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
  },
  {
    id: 'squad',
    label: 'Squad',
    icon: (a) => (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={a ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
];

export const TabBar = ({ active, onNav }: { active: TabId; onNav: (t: TabId) => void }) => (
  <nav className="tab-bar" aria-label="Sections">
    {TAB_DEFS.map((tab) => (
      <a
        key={tab.id}
        href={tab.id === 'terrace' ? '/north-bank/all' : `/?tab=${tab.id}`}
        className={`tab-bar-item${active === tab.id ? ' active' : ''}`}
        onClick={(event) => {
          // Keep ordinary links available for refresh, copy, and modified
          // clicks. A plain primary click gets the app's stateful nav and
          // preserves the existing overlay/back-stack behavior.
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          onNav(tab.id);
        }}
        aria-current={active === tab.id ? 'page' : undefined}
      >
        <span className="tab-bar-icon">{tab.icon(active === tab.id)}</span>
        <span className="tab-bar-label">{tab.label}</span>
      </a>
    ))}
  </nav>
);

const MOBILE_TABS: Array<{id: TabId; label: string; path: string}> = [
  {id:'now',label:'Home',path:'M3 11 12 3l9 8M5 10v11h5v-7h4v7h5V10'},
  {id:'matches',label:'Matchday',path:'M4 5h16v16H4zM8 3v4m8-4v4M4 11h16'},
  {id:'history',label:'History',path:'M6 3v18M6 6h13M6 12h9M6 18h13'},
  {id:'squad',label:'Players',path:'m8 3-5 4 3 5 2-1v10h8V11l2 1 3-5-5-4c0 4-8 4-8 0Z'},
  {id:'terrace',label:'Chat',path:'M4 4h16v13H9l-5 4V4Z'},
];
export const MobileTabBar = ({active,onNav}:{active:TabId;onNav:(tab:TabId)=>void}) => <nav className="mobile-tab-bar" aria-label="Main destinations">
  {MOBILE_TABS.map(tab=><a key={tab.id} href={tab.id==='terrace'?'/north-bank/all':tab.id==='now'?'/':`/?tab=${tab.id}`} aria-current={active===tab.id?'page':undefined} onClick={event=>{if(event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;event.preventDefault();onNav(tab.id);}}><svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={tab.path}/></svg><span>{tab.label}</span></a>)}
</nav>;

// ── Section rule — hairline + micro label, the programme rhythm ──────────────
export const Rule = ({ label, accent, right }: { label: string; accent?: string; right?: ReactNode }) => (
  <div className="rule">
    <span className={`rule-label${accent?.includes('gold') ? ' rule-label--gold' : accent ? ' rule-label--red' : ''}`}>
      {label}
    </span>
    <span className="rule-line" />
    {right}
  </div>
);

/**
 * A timeline year — the unified tappable affordance. When an `onOpenYear` is
 * wired the year becomes a gold "link" chip (matching the honour-chip language)
 * that opens the year view; without one it degrades to the plain, calm year
 * label so the timeline never breaks where the doorway isn't available.
 */
export const YearTag = ({ year, onOpenYear }: { year: string; onOpenYear?: (year: string) => void }) =>
  onOpenYear ? (
    <button className="story-year story-year--link" onClick={() => onOpenYear(year)} aria-label={`Open ${year}`}>
      {year} <span aria-hidden="true">→</span>
    </button>
  ) : (
    <span className="story-year">{year}</span>
  );

// ── Honest-data atoms ────────────────────────────────────────────────────────

/** Tracks whether the device is offline, reacting to online/offline events. */
export function useOffline(): boolean {
  const [offline, setOffline] = useState(() =>
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return offline;
}

/** Renders one inspectable feed timestamp, with an explicit stale/offline state. */
export const StaleBadge = ({ stale = false, asOf = null }: { stale?: boolean; asOf?: string | null }) => {
  const offline = useOffline();
  const label = offline
    ? 'Offline · saved copy'
    : asOf
      ? `${stale ? 'Check needed' : 'Updated'} ${asOfLabel(asOf)}`
      : stale
        ? 'Saved editorial copy'
        : null;
  if (!label) return null;
  return (
    <details className={`source-inspector${stale || offline ? ' source-inspector--warning' : ''}`}>
      <summary>{label}</summary>
      <div className="source-inspector-popover" role="note">
        <strong>{offline ? 'Saved on this device' : stale ? 'Last confirmed copy' : 'Updated edition'}</strong>
        <span>{offline ? 'Reconnect for the latest confirmed edition.' : stale ? 'This item is older than its publishing window. Follow the named official source for the latest position.' : 'This is when COYG last published this edition. Open the story or fixture to inspect its named source.'}</span>
      </div>
    </details>
  );
};

export const EmptyNote = ({ children }: { children: ReactNode }) => (
  <p className="empty-note">{children}</p>
);

/**
 * A compact, reusable attribution for the keyless community score feed.
 * `checkedAt` is COYG's check time; the provider deliberately has no trusted
 * update timestamp in this contract, so the two are never conflated.
 */
export const CommunitySourceNote = ({ report }: { report: CommunityReport }) => {
  const checked = Date.parse(report.checkedAt);
  const checkedLabel = Number.isFinite(checked)
    ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London' }).format(new Date(checked))
    : 'date unavailable';
  return (
    <p className="community-source-note">
      <span>Community update · checked {checkedLabel}</span>{' · '}
      <a href={report.sourceUrl} target="_blank" rel="noopener noreferrer">OpenLigaDB ↗</a>{' · '}
      <a href={report.licenseUrl} target="_blank" rel="noopener noreferrer">ODbL licence ↗</a>
      <span className="community-source-note__status"> · Provider update time unavailable</span>
    </p>
  );
};

/** Sticky header for any detail/overlay: a back control that stays reachable
 *  no matter how far you've scrolled, plus an optional right slot (Share).
 *
 *  `trail` is the ancestor path under the current surface (e.g. ["Squad",
 *  "Gabriel"]) — rendered as a quiet breadcrumb only when the stack is two or
 *  more deep, so a multi-level drill always says "where am I" without shouting.
 */
export const DetailHeader = ({
  onBack,
  backLabel = 'Back',
  trail,
  right,
}: {
  onBack: () => void;
  backLabel?: string;
  trail?: string[];
  right?: ReactNode;
}) => (
  <div className="detail-header">
    <div className="detail-nav">
      <button className="back-btn" onClick={onBack} aria-label={backLabel === 'Back' ? 'Back' : `Back to ${backLabel}`}>
        ← {backLabel}
      </button>
      {trail && trail.length > 1 && (
        <nav className="detail-crumbs" aria-label="Where you are">
          {trail.map((crumb, i) => (
            <span key={`${crumb}-${i}`} className="detail-crumb">
              {i > 0 && <span className="detail-crumb-sep" aria-hidden="true">›</span>}
              {crumb}
            </span>
          ))}
        </nav>
      )}
    </div>
    {right}
  </div>
);

export const PHASE_LABEL: Record<MatchPhase, string> = {
  idle: 'Next up',
  pre: 'Matchday',
  live: 'Live',
  ht: 'Half-time',
  ft: 'Full-time',
};

/** "ARS 2 – 1 NEW"-style score line, opponent-aware of venue. */
export const ScoreLine = ({
  home,
  away,
  opponentShort,
  venue,
}: {
  home: number;
  away: number;
  opponentShort: string;
  venue?: string;
}) => {
  const ours = 'ARS';
  const left = venue === 'A' ? opponentShort : ours;
  const right = venue === 'A' ? ours : opponentShort;
  // Scores are actual home/left then away/right. Venue already controls the
  // corresponding labels, so swapping only the numbers reverses an away win.
  const leftScore = home;
  const rightScore = away;
  return (
    <div className="score-line">
      <span className="score-team">{left}</span>
      <span className="score-nums">
        {leftScore}
        <span className="score-dash">–</span>
        {rightScore}
      </span>
      <span className="score-team">{right}</span>
    </div>
  );
};
